import React, { useState, useContext, useEffect, useRef } from "react";
import { TextField, Button } from "@mui/material";
import FuncionesContext, {
  getAtributo,
  validarRegex,
} from "../context/FuncionesContext";
import { useRefDataStore } from "../../..";
import {
  startCameraScanner,
  waitForVideoElement,
} from "../../../helpers/scanner";

const InputTextEscaner = ({ data, cab, id_elemento, updateid, context }) => {
  const { superSubmit } = useContext(FuncionesContext);
  const c = useContext(context);

  const [update_id] = useRefDataStore(
    (s) => s[data._key + cab.update_id_alias],
  );

  const [valor, setValor] = useState("");
  const [updateId, setUpdateId] = useState(update_id ?? updateid);
  const [error, setError] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState("");
  const [isProcessingScan, setIsProcessingScan] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState("");
  const [hasFlash, setHasFlash] = useState(false);
  const [isFlashOn, setIsFlashOn] = useState(false);

  const attemptSubmit = useRef(null);
  const videoRef = useRef(null);
  const scannerControlsRef = useRef(null);
  const scannerSessionRef = useRef(0);
  const scannerLockRef = useRef(false);
  const scannerOpenedByFocusRef = useRef(false);

  const isMobileDevice = (() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return false;
    }

    const userAgent =
      navigator.userAgent || navigator.vendor || window.opera || "";

    return (
      /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(userAgent) ||
      window.matchMedia?.("(max-width: 900px)").matches
    );
  })();

  const supportsCamera =
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia);

  useEffect(() => {
    return () => {
      scannerSessionRef.current += 1;
      if (scannerControlsRef.current?.stop) {
        scannerControlsRef.current.stop();
      }
    };
  }, []);

  const stopScanner = () => {
    scannerSessionRef.current += 1;
    if (scannerControlsRef.current?.stop) {
      scannerControlsRef.current.stop();
      scannerControlsRef.current = null;
    }

    scannerLockRef.current = false;
    setIsScannerOpen(false);
    setIsProcessingScan(false);
    setCameras([]);
    setSelectedCamera("");
    setHasFlash(false);
    setIsFlashOn(false);
  };

  const handleGuardarValor = async (value) => {
    setValor(value);

    if (!validarRegex(value, cab.validacion_regex, cab)) {
      setError(true);
      return;
    }

    setError(false);
    const { id_a } = cab;

    superSubmit({
      valor: value,
      id_a,
      update_id: updateId,
      cab,
      data,
    })
      .then((result) => {
        if (!updateId) {
          setUpdateId(result.data.registro.id);
        }
        cerrarModal();
      })
      .catch((err) => {
        console.log("Cancelado ", err);
        setValor("");
      });
  };

  const applyScannedValue = (rawValue) => {
    let value = String(rawValue ?? "").trim();
    if (!value) return;

    if (cab.solo_mayus === "s") {
      value = value.toUpperCase();
    }

    if (cab.maximo_caracteres !== "" && cab.maximo_caracteres) {
      value = value.slice(0, parseInt(cab.maximo_caracteres));
    }

    setScannerError("");
    setError(false);
    setValor(value);
    handleGuardarValor(value);
  };

  const handleDetectedCode = (rawValue) => {
    if (!rawValue || scannerLockRef.current) return;

    scannerLockRef.current = true;
    applyScannedValue(rawValue);
    stopScanner();
  };

  const openMobileScanner = async () => {
    setScannerError("");

    if (!isMobileDevice) {
      return;
    }

    if (!supportsCamera) {
      setScannerError("El navegador no permite acceder a la camara en vivo.");
      return;
    }

    const session = scannerSessionRef.current + 1;
    scannerSessionRef.current = session;
    scannerLockRef.current = false;
    setIsScannerOpen(true);
    setIsProcessingScan(true);

    try {
      const video = await waitForVideoElement(videoRef);
      if (scannerSessionRef.current !== session) return;

      const controls = await startCameraScanner("multi-format", {
        video,
        onDetected: handleDetectedCode,
        formatos: getAtributo("formatos", data, cab),
      });

      if (scannerSessionRef.current !== session) {
        controls.stop();
        return;
      }

      scannerControlsRef.current = controls;
      const availableCameras = await controls.listCameras?.();
      const activeDeviceId = controls
        .getStream?.()
        ?.getVideoTracks?.()[0]
        ?.getSettings?.().deviceId;

      setCameras(availableCameras ?? []);
      setSelectedCamera(activeDeviceId || "");
      setHasFlash((await controls.hasFlash?.()) ?? false);
      setIsProcessingScan(false);
    } catch (cameraError) {
      setScannerError(
        "No se pudo iniciar el lector de codigos. Revisa el permiso de camara.",
      );
      stopScanner();
    }
  };

  const handleCameraChange = async (event) => {
    const cameraId = event.target.value;
    setSelectedCamera(cameraId);
    setIsProcessingScan(true);

    try {
      await scannerControlsRef.current?.setCamera?.(cameraId);
      setHasFlash((await scannerControlsRef.current?.hasFlash?.()) ?? false);
      setIsFlashOn(false);
      setScannerError("");
    } catch (cameraError) {
      setScannerError("No se pudo cambiar a esa camara.");
    } finally {
      setIsProcessingScan(false);
    }
  };

  const toggleFlash = async () => {
    if (!scannerControlsRef.current) return;

    try {
      await scannerControlsRef.current.toggleFlash?.();
      setIsFlashOn(scannerControlsRef.current.isFlashOn?.() ?? false);
    } catch (flashError) {
      setScannerError("La linterna no esta disponible en esta camara.");
    }
  };

  const handleFocus = () => {
    const abrirCamaraOnFocus =
      getAtributo("abrir_camara_onFocus", data, cab) === "s";

    if (
      !abrirCamaraOnFocus ||
      !isMobileDevice ||
      scannerOpenedByFocusRef.current ||
      isScannerOpen
    ) {
      return;
    }

    scannerOpenedByFocusRef.current = true;
    openMobileScanner();
  };

  const handleBlur = () => {
    scannerOpenedByFocusRef.current = false;
  };

  const handleInput = (e) => {
    const regexp = getAtributo("validacion_regex", data, cab);
    const timeout = () =>
      setTimeout(() => {
        if (!validarRegex(value, regexp, cab)) {
          setError(true);
          return;
        }
        setError(false);
        handleGuardar(e);
      }, 100);

    if (attemptSubmit.current) {
      clearTimeout(attemptSubmit.current);
      attemptSubmit.current = null;
    }
    const { value } = e.target;
    setValor(value);
    setError(false);
    attemptSubmit.current = timeout();
  };

  const className = getAtributo("className", data, cab);
  const nombre = getAtributo("nombre", data, cab);

  const cerrarModal = () => {
    if (cab.cerrar_modal !== "s") return;
    const modal = c?.modalProps;
    if (modal?.esModal && modal?.open) {
      modal.cerrar();
    }
  };

  const handleGuardar = async (e) => {
    const { value } = e.target;
    handleGuardarValor(value);
  };

  return (
    <>
      {nombre && nombre.trim() ? (
        <div className="vista_label">
          <p
            style={{
              maxHeight: "25px",
              position: "relative",
              top: "2px",
              wordBreak: "break-all",
            }}
            className="suW"
          >
            {nombre}:
          </p>
        </div>
      ) : null}
      <TextField
        id={id_elemento}
        type={"text"}
        placeholder={cab.placeholder}
        value={valor}
        onChange={handleInput}
        error={error}
        helperText={error ? "Formato invalido" : ""}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={`${className} input-text-escaner`}
        inputProps={{
          "data-input-configuracion-id": cab.id_a,
          "data-input-clave-envio": cab.clave_envio || undefined,
          "data-input-campo-alias": cab.campo_alias || undefined,
          maxLength:
            cab.maximo_caracteres !== "" ? parseInt(cab.maximo_caracteres) : 60,
        }}
      />
      {scannerError ? (
        <div
          style={{
            marginTop: "6px",
            color: "#b42318",
            fontSize: "0.8rem",
          }}
        >
          {scannerError}
        </div>
      ) : null}
      {isScannerOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.85)",
            zIndex: 1600,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px",
            gap: "12px",
          }}
        >
          <video
            ref={videoRef}
            muted
            playsInline
            autoPlay
            style={{
              width: "100%",
              maxWidth: "420px",
              borderRadius: "16px",
              background: "#000",
            }}
          />
          <div
            style={{
              color: "#fff",
              textAlign: "center",
              fontSize: "0.95rem",
            }}
          >
            {isProcessingScan
              ? "Abriendo camara..."
              : "Apunta la camara al codigo o QR"}
          </div>
          {cameras.length > 1 ? (
            <select
              value={selectedCamera}
              onChange={handleCameraChange}
              aria-label="Seleccionar camara"
              style={{
                width: "100%",
                maxWidth: "420px",
                padding: "10px 12px",
                borderRadius: "8px",
                background: "#fff",
              }}
            >
              <option value="" disabled>
                Seleccionar camara
              </option>
              {cameras.map((camera) => (
                <option key={camera.id} value={camera.id}>
                  {camera.label}
                </option>
              ))}
            </select>
          ) : null}
          {hasFlash ? (
            <Button
              variant="contained"
              onClick={toggleFlash}
              style={{ background: "#f5c542", color: "#111" }}
            >
              {isFlashOn ? "Apagar linterna" : "Encender linterna"}
            </Button>
          ) : null}
          <Button
            variant="contained"
            onClick={stopScanner}
            style={{
              background: "#fff",
              color: "#111",
            }}
          >
            Cancelar
          </Button>
        </div>
      ) : null}
    </>
  );
};

export default InputTextEscaner;
